import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AuthData, AuthStateService } from '@arya/auth/data-access';
import type { LoginResponse } from '@arya/auth/domain';
import { DEV_INVESTOR_PASSWORD } from '@arya/shared/dev-accounts';

import { firstValueFrom } from 'rxjs';

import { type AuthenticatorCodes, DEMO_AUTHENTICATOR_URL } from './demo-authenticator';
import { DemoOutbox } from './demo-outbox';
import type { DemoPersona } from './demo-persona';

const DEV_TURNSTILE_TOKEN = 'dev-demo-overlay';

@Injectable()
export class DemoSignIn {
  private readonly authState = inject(AuthStateService);
  private readonly authData = inject(AuthData);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly outbox = inject(DemoOutbox);

  public async signOutTo(target: string): Promise<void> {
    if (this.authState.currentUser()) {
      await this.authState.logout();
    }
    await this.router.navigateByUrl(target);
  }

  public async signInAs(persona: DemoPersona, target = persona.home): Promise<void> {
    if (this.authState.currentUser()) {
      await this.authState.logout();
    }
    await this.authenticate(persona);
    await this.router.navigateByUrl(target);
  }

  public async authenticate(persona: DemoPersona): Promise<void> {
    const response = await this.authState.login(
      persona.email,
      persona.password ?? DEV_INVESTOR_PASSWORD,
      DEV_TURNSTILE_TOKEN
    );
    if (response.mfaRequired) {
      await this.verifySecondFactor(persona.email, response);
    }
    this.outbox.markSeen();
    await this.authState.refresh();
  }

  private async verifySecondFactor(email: string, response: LoginResponse): Promise<void> {
    const totp = response.availableMethods?.includes('totp') ? await this.authenticatorCode(email) : undefined;
    const code = totp ?? (await this.emailCode(email, response));
    await firstValueFrom(
      this.authData.mfaVerify({
        mfaToken: response.token,
        type: totp === undefined ? 'email' : 'totp',
        code,
        turnstileToken: DEV_TURNSTILE_TOKEN,
      })
    );
  }

  private async authenticatorCode(email: string): Promise<string | undefined> {
    const { codes } = await firstValueFrom(this.http.get<AuthenticatorCodes>(DEMO_AUTHENTICATOR_URL));
    return codes.find((entry) => entry.email === email)?.code;
  }

  private async emailCode(email: string, response: LoginResponse): Promise<string> {
    if (response.codeSentVia !== 'email') {
      await firstValueFrom(
        this.authData.loginResend({ mfaToken: response.token, turnstileToken: DEV_TURNSTILE_TOKEN })
      );
    }
    return this.outbox.latestCode('email', email) ?? '';
  }
}
